import type { Holiday } from './types.js';

const MS_PER_DAY = 86_400_000;

/** Strip the time-of-day so every civil date lives at 00:00 UTC. */
export function toCivilDate(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

export function addCivilDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * MS_PER_DAY);
}

export function isWeekend(date: Date): boolean {
  const day = date.getUTCDay();
  return day === 0 || day === 6;
}

/** `YYYY-MM-DD` string suitable for use as a Map key over civil dates. */
export function civilDateKey(date: Date): string {
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;
}

export function buildHolidayMap(holidays: readonly Holiday[]): Map<string, Holiday> {
  const m = new Map<string, Holiday>();
  for (const h of holidays) {
    m.set(civilDateKey(toCivilDate(h.date)), h);
  }
  return m;
}

export function isHoliday(date: Date, holidayMap: Map<string, Holiday>): Holiday | null {
  return holidayMap.get(civilDateKey(date)) ?? null;
}

export function isBusinessDay(date: Date, holidayMap: Map<string, Holiday>): boolean {
  if (isWeekend(date)) return false;
  if (isHoliday(date, holidayMap) !== null) return false;
  return true;
}

export function nextBusinessDay(date: Date, holidayMap: Map<string, Holiday>): Date {
  let d = addCivilDays(date, 1);
  while (!isBusinessDay(d, holidayMap)) d = addCivilDays(d, 1);
  return d;
}

export function prevBusinessDay(date: Date, holidayMap: Map<string, Holiday>): Date {
  let d = addCivilDays(date, -1);
  while (!isBusinessDay(d, holidayMap)) d = addCivilDays(d, -1);
  return d;
}

export function rollForwardToBusinessDay(date: Date, holidayMap: Map<string, Holiday>): Date {
  let d = date;
  while (!isBusinessDay(d, holidayMap)) d = addCivilDays(d, 1);
  return d;
}

/** Civil dates from `min(a,b)` to `max(a,b)`, inclusive at both ends. */
export function eachCivilDayInclusive(a: Date, b: Date): Date[] {
  const start = a.getTime() <= b.getTime() ? a : b;
  const end = a.getTime() <= b.getTime() ? b : a;
  const out: Date[] = [];
  for (let d = start; d.getTime() <= end.getTime(); d = addCivilDays(d, 1)) {
    out.push(d);
  }
  return out;
}

/** Add calendar years/months with end-of-month clamping (Jan 31 + 1 month becomes Feb 28/29). */
export function addCalendarYearsMonths(date: Date, years: number, months: number): Date {
  const y = date.getUTCFullYear() + years;
  const m = date.getUTCMonth() + months;
  const d = date.getUTCDate();
  const shifted = new Date(Date.UTC(y, m, 1));
  const daysInTargetMonth = new Date(
    Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, 0),
  ).getUTCDate();
  shifted.setUTCDate(Math.min(d, daysInTargetMonth));
  return shifted;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}
